import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { GenericErrorModal } from '../../../../components/errors/GenericErrorModal';
import { ACTION_MODAL_TYPES, ActionModal } from '../../../../DesignSystem/ActionModal';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { useInvoiceRecipientsQuery } from '../../../../hooks/query/useInvoiceRecipientsQuery';
import { useStore } from '../../../../store';
import { CreateNewClient } from '../../InvoiceCreation/components/SectionClient/CreateNewClient';
import { IInvoiceRecipient } from '../../InvoiceCreation/types/types';
import { ItemsTableHeader } from '../shared/ItemsTableHeader';
import { ClientsTable } from './components/ClientsTable';
import { TEXTS } from './constants';
import { useDeleteRecipient } from './hooks/userClientDelete';
import { Layout } from '../../../../components/Layout/Layout';
import { featureAccess, LimitedFeatureType } from '../../../../services/appAccess.service';
import { FeatureAccess } from '../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

enum ClientsActivePopup {
  DeleteItem,
  EditItem,
  CreateItem,
  None,
}

export const ClientsHub = () => {
  const {
    DELETE_POPUP,
    MAIN_TITLE,
    SEARCH_BAR_PLACEHOLDER,
    NEW_ITEM_BUTTON,
    // ITEM_MENU,
  } = TEXTS.CLIENTS_HUB;
  const { t } = useTranslation();
  const { t: errorsEN } = useTranslation('errorsEN');

  const {
    customer: { externalId },
  } = useStore((state) => state.summary);
  const [activePopup, setActivePopup] = useState<ClientsActivePopup | null>(null);
  const [showEmptyState, setShowEmptyState] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const selectedRecipient = useRef<IInvoiceRecipient | undefined>(undefined);
  const invoicesPermission = featureAccess(null, LimitedFeatureType.Invoices);

  const {
    data: allRecipients,
    isLoading: isLoadingClients,
    isRefetching: isRefetchingClients,
    refetch: refetchClients,
  } = useInvoiceRecipientsQuery({
    customerId: externalId,
    onError: (err: Error) => {
      setErrorMessage(err.message);
    },
  });

  const { mutateAsync: mutateDeleteRecipient, isLoading: isDeletingProduct } = useDeleteRecipient();

  const reloadRecipients = () => {
    refetchClients().then((res) => {
      setActivePopup(ClientsActivePopup.None);
      setShowEmptyState(res.data?.length === 0);
    });
  };
  useEffect(() => {
    reloadRecipients();
  }, []);
  const handleDeleteClick = () => {
    const variables = {
      customerId: externalId,
      recipientId: selectedRecipient.current?.externalId || '',
    };
    mutateDeleteRecipient(variables).then(() => {
      reloadRecipients();
    });
  };
  const filteredRecipient = () =>
    allRecipients?.filter(
      (recipient: IInvoiceRecipient) =>
        recipient.name.toLowerCase().includes(searchText.toLowerCase()) ||
        recipient.email.toLowerCase().includes(searchText.toLowerCase()),
    );

  const createNewForm = (
    <CreateNewClient
      initialName=''
      handlePostSubmit={() => reloadRecipients()}
      onClose={() => setActivePopup(ClientsActivePopup.None)}
    />
  );

  const skeleton = <Skeleton count={10} height={60} />;

  const editForm = (
    <CreateNewClient
      initialName=''
      client={selectedRecipient.current}
      handlePostSubmit={() => reloadRecipients()}
      onClose={() => setActivePopup(ClientsActivePopup.None)}
    />
  );
  const deletePopup = (
    <ActionModal
      title={t(DELETE_POPUP.TITLE)}
      description={t(DELETE_POPUP.DESCRIPTION)}
      mainButtonTitle={t(DELETE_POPUP.DELETE_BUTTON)}
      cancelButtonTitle={t(DELETE_POPUP.CANCEL_BUTTON)}
      onSubmit={handleDeleteClick}
      onCancel={() => setActivePopup(ClientsActivePopup.None)}
      isLoading={isDeletingProduct || isRefetchingClients}
      modalType={ACTION_MODAL_TYPES.DESTRUCTIVE}
    />
  );
  const errorModal = errorMessage && (
    <UserMessage onClick={() => setErrorMessage(null)} id=''>
      <GenericErrorModal
        title={errorsEN('GENERIC_MODAL.TITLE')}
        description={errorsEN('GENERIC_MODAL.SUBTITLE')}
        buttonTitle={errorsEN('GENERIC_MODAL.CTA')}
        onActionClick={() => setErrorMessage(null)}
      />
    </UserMessage>
  );
  return (
    <Layout wrapperClassName='pt-[55px] pl-[72px] w-screen pr-[32px] ml-[100px] pc-md:ml-[256px]'>
      <div>
        {activePopup === ClientsActivePopup.CreateItem && createNewForm}
        {activePopup === ClientsActivePopup.EditItem && selectedRecipient.current != null && editForm}
        {activePopup === ClientsActivePopup.DeleteItem && deletePopup}
        {errorModal}
        <ItemsTableHeader
          title={t(MAIN_TITLE)}
          searchPlaceholder={t(SEARCH_BAR_PLACEHOLDER)}
          addButtonTitle={t(NEW_ITEM_BUTTON)}
          addButtonDisabled={invoicesPermission !== FeatureAccess.READ_WRITE}
          onSearch={setSearchText}
          onAddNewClick={() => setActivePopup(ClientsActivePopup.CreateItem)}
          totalProductsCount={allRecipients?.length || 0}
        />
        {isRefetchingClients || isLoadingClients ? (
          skeleton
        ) : (
          <ClientsTable
            clients={filteredRecipient()}
            onAddNewClick={() => setActivePopup(ClientsActivePopup.CreateItem)}
            showEmptyState={showEmptyState}
            onDeleteClick={(recipient) => {
              selectedRecipient.current = recipient;
              setActivePopup(ClientsActivePopup.DeleteItem);
            }}
            onEditClick={(recipient) => {
              selectedRecipient.current = recipient;
              setActivePopup(ClientsActivePopup.EditItem);
            }}
          />
        )}
      </div>
    </Layout>
  );
};
